import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import { BiTask } from "react-icons/bi";
import { IoMdLogOut } from "react-icons/io";
import { BsFillHeartPulseFill } from "react-icons/bs";
import { FiUser } from "react-icons/fi";
import { IoLocationOutline } from "react-icons/io5";
import { GoDotFill } from "react-icons/go";
import { LuNotepadText, LuClock3 } from "react-icons/lu";
import Backdrop from "@mui/material/Backdrop";
import Confirmation from "../../common/components/Confirmation";
import AlertMessage from "../../common/components/AlertMessage";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL";
import { getSessionContext } from "../../common/utils/sessionContext";
import { removeManyFromLocalStorage } from "../../common/utils/localStorage";
import * as Sentry from "@sentry/react";
import "../../styles/ResidentHome.css";

const ResidentHome = () => {
  const navigate = useNavigate();
  const [showTodoPopup, setShowTodoPopup] = useState(false);
  const [alertStatus, setAlertStatusBase] = useState("");
  const [loading, setLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const sessionContext = getSessionContext();
  const checkinDate = sessionContext.checkinTime ? new Date(sessionContext.checkinTime) : null;
  const hasValidCheckinTime = Boolean(checkinDate && !Number.isNaN(checkinDate.getTime()));

  useEffect(() => {
    if (!hasValidCheckinTime) {
      setElapsedSeconds(0);
      return;
    }

    const updateElapsed = () => {
      const seconds = Math.max(0, Math.floor((Date.now() - checkinDate.getTime()) / 1000));
      setElapsedSeconds(seconds);
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);
    return () => window.clearInterval(intervalId);
  }, [hasValidCheckinTime, sessionContext.checkinTime]);

  const formatDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return hours > 0 ? `${String(hours).padStart(2, "0")}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
  };

  const residentInitials =
    (sessionContext.residentName || "")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "CL";

  const residentRoomNumber = (() => {
    const directRoom = localStorage.getItem("RoomNumber") || localStorage.getItem("roomNumber") || localStorage.getItem("RoomNo") || localStorage.getItem("roomNo") || localStorage.getItem("ResidentRoomNumber") || localStorage.getItem("residentRoomNumber");
    if (directRoom && directRoom.trim()) {
      return directRoom.trim();
    }

    const residentInfoRaw = localStorage.getItem("ResidentInfo");
    if (residentInfoRaw) {
      try {
        const residentInfo = JSON.parse(residentInfoRaw);
        const infoRoom = residentInfo?.roomNumber || residentInfo?.RoomNumber || residentInfo?.roomNo || residentInfo?.RoomNo || residentInfo?.room;
        if (infoRoom && String(infoRoom).trim()) {
          return String(infoRoom).trim();
        }
      } catch (error) {}
    }

    return "222 B";
  })();

  const menuCards = [
    {
      label: "Tasks",
      subLabel: "View and update tasks",
      icon: <BiTask className="newresident-menu-icon" />,
      path: "resident-task",
    },
    {
      label: "Basic Info",
      subLabel: "View client information",
      icon: <FiUser className="newresident-menu-icon" />,
      path: "resident-notes",
    },
    {
      label: "Notes",
      subLabel: "View and add shift notes",
      icon: <LuNotepadText className="newresident-menu-icon" />,
      path: "notes",
    },
    {
      label: "Vitals",
      subLabel: "View and add vitals",
      icon: <BsFillHeartPulseFill className="newresident-menu-icon" />,
      path: "vitals",
    },
  ];

  const handleChange = (e, pathname) => {
    e.preventDefault();
    navigate("/" + pathname);
  };

  const openTodo = () => {
    setShowTodoPopup(true);
  };

  const closeTodoConfirmation = () => {
    setShowTodoPopup(false);
  };

  const confirmationCallback = (e) => {
    e.preventDefault();
    setLoading(true);
    const { caregiverCheckinId } = getSessionContext();
    const data = { caregiverCheckinId };

    APIService.postData(URL.caregiverCheckout, data)
      .then((responseData) => {
        if (responseData.code == 200) {
          setShowTodoPopup(false);
          removeManyFromLocalStorage(["ResidentName", "ResidentId", "roomNumber", "CaregiverCheckinId", "CheckinTime", "shiftId"]);
          navigate("/home");
        } else {
          setAlertStatusBase({ message: responseData.message, status: responseData.code, origin: "center" });
        }
        setLoading(false);
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoading(false);
        setAlertStatusBase({ message: "Error while checkin resident", status: "400", origin: "center" });
      });
  };

  const closeAlert = () => {
    setAlertStatusBase("");
  };

  return (
    <>
      <Header noBorderBottom />
      <div className="home-container newresident-page">
        <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
          <img src={require("../../assets/btTree.png")} className="loading-img" alt="Loading" />
        </Backdrop>
        {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}
        <div className="home-inner-container newresident-layout">
          <div className="newresident-visit-card bg-visit-gradient">
            <div className="newresident-visit-fx" aria-hidden="true" />
            <div className="newresident-visit-top">
              <p className="newresident-visit-label">Current Visit</p>
              <div className="newresident-community-tag">
                <IoLocationOutline />
                <span>{sessionContext.communityName || "Community"}</span>
              </div>
            </div>
            <div className="newresident-profile-row">
              <div className="newresident-initials-badge">{residentInitials}</div>
              <div className="newresident-profile-text">
                <h2 className="newresident-title">{sessionContext.residentName}</h2>
                <p className="newresident-profile-subtitle">Unit - {residentRoomNumber}</p>
              </div>
            </div>
            <div className="newresident-visit-divider" />
            <div className="newresident-arrived-row">
              <div className="newresident-arrived-pill">
                <GoDotFill />
                <span className="newresident-checkin-time">Checked in at {hasValidCheckinTime ? checkinDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "--:--"}</span>
              </div>
              <div className="newresident-runtime-block">
                <span className="newresident-runtime-label">
                  <LuClock3 />
                  Duration
                </span>
                <span className="newresident-runtime-value">{formatDuration(elapsedSeconds)}</span>
              </div>
            </div>
          </div>

          <div className="newresident-grid">
            {menuCards.map((menu) => (
              <div key={menu.label} className="home-menus newresident-menu" onClick={(e) => handleChange(e, menu.path)}>
                <div className="newresident-menu-icon-wrap">{menu.icon}</div>
                <span>{menu.label}</span>
                <small>{menu.subLabel}</small>
              </div>
            ))}
          </div>

          <div className="home-menus newresident-checkout bg-visit-gradient" onClick={openTodo}>
            <IoMdLogOut className="newresident-checkout-icon" />
            <span>Check-Out</span>
          </div>
        </div>

        <Confirmation username={sessionContext.residentName} content="Are you sure you want to check-out?" open={showTodoPopup} closeConfirmation={closeTodoConfirmation} processCallback={confirmationCallback} />
      </div>
    </>
  );
};

export default ResidentHome;
